'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, t } from '@lingui/macro';
import { Button, MenuItem, Stack, ToggleButton } from '@mui/material';
import { SelectInput, ToggleButtonGroupInput } from '@prismo-io/design-system';
import {
  LanguageCode,
  type ToolsOrganizationsT,
  type UsersT,
} from '@prismo-io/schemas';
import { type FC, useCallback, useMemo } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { type Config, DEFAULT_CONFIG, ModeSchema, TypeSchema } from './schema';
import { useDownloadPDFUser } from './use-download-pdf-user';

type Props = {
  organization?: Pick<ToolsOrganizationsT, 'id' | 'email'>;
  users: Pick<UsersT, 'id' | 'full_name'>[];
  config?: Config;
  onFinish: () => void;
};

const FormSchema = z.object({
  type: TypeSchema,
  locale: LanguageCode,
  mode: ModeSchema,
});

type FormSchemaT = z.infer<typeof FormSchema>;

export const DownloadPdfForm: FC<Props> = (props) => {
  const { organization, users, onFinish, config } = props;

  const { cv, talent, expert, mode } = { ...DEFAULT_CONFIG, ...config };

  const hasManyTypes = useMemo(
    () => talent?.visible || expert?.visible,
    [talent, expert]
  );

  const download = useDownloadPDFUser();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid, isSubmitting },
  } = useForm<FormSchemaT>({
    mode: 'all',
    resolver: zodResolver(FormSchema),
    defaultValues: {
      type: TypeSchema.Enum.CV,
      locale: LanguageCode.Enum.fr,
      mode: mode === 'anon' ? ModeSchema.Enum.anon : ModeSchema.Enum.full,
    },
  });

  const watchedMode = watch('mode');

  const onSubmit: SubmitHandler<FormSchemaT> = useCallback(
    (data) => {
      download({
        users,
        organization,
        ...data,
      } as any);

      onFinish();
    },
    [onFinish, users, organization, download]
  );

  return (
    <Stack
      component="form"
      gap={2}
      onSubmit={handleSubmit(onSubmit)}
      padding={2}
      noValidate
    >
      {/* <Typography fontWeight="semibold" alignSelf="flex-start">
        <Trans>Configuration du rapport à générer</Trans>
      </Typography> */}

      <SelectInput
        control={control}
        disabled={isSubmitting || !hasManyTypes}
        name="type"
        label={t`Type`}
        size="small"
        required
      >
        {cv?.visible && (
          <MenuItem value={TypeSchema.Enum.CV} disabled={cv.disabled}>
            <Trans>CV PRISMO</Trans>
          </MenuItem>
        )}
        {talent?.visible && (
          <MenuItem value={TypeSchema.Enum.TALENT} disabled={talent.disabled}>
            <Trans>Talent</Trans>
          </MenuItem>
        )}
        {expert?.visible && (
          <MenuItem value={TypeSchema.Enum.EXPERT} disabled={expert.disabled}>
            <Trans>Expert</Trans>
          </MenuItem>
        )}
      </SelectInput>

      <SelectInput
        control={control}
        disabled={isSubmitting}
        name="locale"
        label={t`Langue`}
        size="small"
        required
      >
        <MenuItem value={LanguageCode.Enum.fr}>
          <Trans>Français</Trans>
        </MenuItem>
        <MenuItem value={LanguageCode.Enum.en}>
          <Trans>Anglais</Trans>
        </MenuItem>
      </SelectInput>

      {mode === 'all' && (
        <ToggleButtonGroupInput
          control={control}
          name="mode"
          size="small"
          exclusive
          disabled={isSubmitting}
          label={t`Anonymisation des données`}
          unselectable="off"
          sx={{ flex: 1 }}
        >
          <ToggleButton
            value={ModeSchema.Enum.anon}
            size="small"
            color="primary"
            disabled={watchedMode === ModeSchema.Enum.anon}
          >
            <Trans>Oui</Trans>
          </ToggleButton>
          <ToggleButton
            value={ModeSchema.Enum.full}
            disabled={watchedMode === ModeSchema.Enum.full}
            size="small"
            color="primary"
          >
            <Trans>Non</Trans>
          </ToggleButton>
        </ToggleButtonGroupInput>
      )}

      <Stack alignItems="center" justifyContent="center">
        <Button disabled={isSubmitting || !isValid} type="submit" size="small">
          <Trans>Télécharger</Trans>
        </Button>
      </Stack>
    </Stack>
  );
};
